/**
 * ToolCallChip — one tool call in the Activity View: tool, one-line result, duration. Click opens
 * the arguments, the policy decision and the full result JSON. Citation chips open the part dossier.
 */

import {
  CircleCheck, CircleX, LoaderCircle, Hand, SkipForward, ChevronRight, ChevronDown, ShieldAlert,
} from 'lucide-react';
import SpecialistDot from './SpecialistDot.jsx';
import { formatDuration, oneLine } from './agentUi.js';
import './AgentStream.css';

const CALL_STATUS = {
  running: { Icon: LoaderCircle, label: 'Running', tone: 'accent', spin: true },
  awaiting_approval: { Icon: Hand, label: 'Waiting for approval', tone: 'decide' },
  ok: { Icon: CircleCheck, label: 'Done', tone: 'good' },
  error: { Icon: CircleX, label: 'Error', tone: 'bad' },
  denied: { Icon: ShieldAlert, label: 'Denied by policy', tone: 'bad' },
  rejected: { Icon: CircleX, label: 'Rejected', tone: 'warn' },
  skipped: { Icon: SkipForward, label: 'Skipped', tone: 'faint' },
};

const pretty = (v) => {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

export default function ToolCallChip({ call, specialist = null, expanded = false, onToggle, onOpenCitation }) {
  if (!call) return null;
  const s = CALL_STATUS[call.status] ?? CALL_STATUS.running;
  const StatusIcon = s.Icon;
  const summary = call.summary || (call.error ? `Error: ${call.error}` : s.label);
  const citations = (call.citations || []).filter((c) => c && (c.cad_id || c.id));

  return (
    <div className={`astream__call is-${call.status}${expanded ? ' is-open' : ''}`}>
      <button
        type="button"
        className="astream__call-head"
        aria-expanded={expanded}
        onClick={() => onToggle?.(call.id)}
        title={expanded ? 'Hide details' : 'Show arguments and result'}
      >
        <span className={`astream__call-icon is-${s.tone}${s.spin ? ' is-spin' : ''}`} title={s.label}>
          <StatusIcon size={13} strokeWidth={2} aria-hidden="true" />
          <span className="agent-sr">{s.label}</span>
        </span>
        <SpecialistDot specialist={specialist} size={6} />
        <span className="astream__call-tool">{call.tool}</span>
        <span className="astream__call-summary">{oneLine(summary, 140)}</span>
        <span className="astream__call-time">{formatDuration(call.duration_ms)}</span>
        {call.recorded && <span className="astream__chip-tag">Recorded</span>}
        {expanded ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
      </button>

      {citations.length > 0 && (
        <div className="astream__cites">
          {citations.map((c, i) => (
            <button
              key={`${c.kind}-${c.id ?? c.cad_id}-${i}`}
              type="button"
              className="astream__cite"
              disabled={!c.cad_id || typeof onOpenCitation !== 'function'}
              title={c.text || (c.cad_id ? 'Open the part dossier' : c.id)}
              onClick={() => c.cad_id && onOpenCitation?.({ cad_id: c.cad_id })}
            >
              {c.cad_id ? c.cad_id : c.id}
            </button>
          ))}
        </div>
      )}

      {expanded && (
        <div className="astream__call-body">
          {call.decision && (
            <div className="astream__call-decision">
              <span className="astream__kicker">Decision</span>
              <span>{call.decision.outcome}{call.decision.rule_id ? ` · ${call.decision.rule_id}` : ''}</span>
              {(call.decision.reasons || []).map((r) => <span key={r} className="astream__call-reason">{r}</span>)}
            </div>
          )}
          <div className="astream__kicker">Arguments</div>
          <pre className="astream__json">{pretty(call.args ?? {})}</pre>
          <div className="astream__kicker">Result</div>
          <pre className="astream__json">{call.result === null || call.result === undefined ? (call.error ?? '—') : pretty(call.result)}</pre>
        </div>
      )}
    </div>
  );
}
